import { DossierDetailsContent, DossierDetailsHeader, DossierDetailsPanel } from "@/app/pages/dossier/dossier-details-panel.tsx";
import { BetrokkenStation } from "@/entities/betrokken-stations";
import { BeheerVestiging, useGetLazyBeheerVestigingenByStations, usePostDossierVestigingen, Vestiging } from "@/entities/vestiging";
import { Button, EmptyState, Icon } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { useCallback, useState } from "react";
import { AddStationDialog } from "./add-station-dialog";
import { FaciliteitenDetailsForStation } from "./faciliteiten-details-for-station";

type BeheerVestigingenByStation = Record<string, BeheerVestiging[]>;

function mapVestigingen(beheerVestigingen: BeheerVestiging[]): Vestiging[] {
    return Vestiging.sort(
        beheerVestigingen.map((beheerVestiging) => ({
            beheerVestiging,
            currentOpeningstijden: BeheerVestiging.getOpeningstijdenVandaag(beheerVestiging),
        }))
    );
}

function enrichVestigingen(vestigingen: Vestiging[], localOpenTotUpdates: Record<number, string>): Vestiging[] {
    return vestigingen.map((vestiging) => ({
        ...vestiging,
        isSelectableForSelectAll:
            !BeheerVestiging.isAhToGo(vestiging.beheerVestiging) && Vestiging.isOpen(vestiging) && Vestiging.canRequestStart(vestiging),
        currentOpeningstijden: localOpenTotUpdates[vestiging.beheerVestiging.id]
            ? {
                  ...vestiging.currentOpeningstijden,
                  openTot: vestiging.beheerVestiging.isNsRetailVestiging
                      ? localOpenTotUpdates[vestiging.beheerVestiging.id]
                      : vestiging.currentOpeningstijden.openTot,
                  aangepasteOpenTot: localOpenTotUpdates[vestiging.beheerVestiging.id],
                  aangepasteOpenTotStatus:
                      vestiging.beheerVestiging.isNsRetailVestiging && localOpenTotUpdates[vestiging.beheerVestiging.id]
                          ? "Actief"
                          : vestiging.currentOpeningstijden.aangepasteOpenTotStatus,
              }
            : vestiging.currentOpeningstijden,
    }));
}

type NieuweAanvraagRegievoerderProps = {
    vestigingen: Vestiging[];
    beheerVestigingen: BeheerVestiging[];
    betrokkenStations: BetrokkenStation[];
    dossierId: string;
};

export function NieuweAanvraagRegievoerder({
    vestigingen,
    beheerVestigingen,
    dossierId,
    betrokkenStations,
}: Readonly<NieuweAanvraagRegievoerderProps>) {
    const [vestigingenIdSelection, setVestigingenIdSelection] = useState<number[]>([]);
    const [addStationDialogOpen, setAddStationDialogOpen] = useState(false);
    const [manualStations, setManualStations] = useState<BeheerVestigingenByStation>({});
    const [localOpenTotUpdates, setLocalOpenTotUpdates] = useState<Record<number, string>>({});
    const [requestError, setRequestError] = useState<Error | null>(null);
    const requestVestigingenMutation = usePostDossierVestigingen();
    const getLazyVestigingenByStations = useGetLazyBeheerVestigingenByStations();

    // We need to display a combination of aangevraagdeVestigingen and beheerVestigingen.
    // If there is no aangevraagdeVestiging for a beheerVestiging (i.e., no aanvraag yet),
    // we create a vestiging object from the beheerVestiging so it can be shown and selected.

    // Filter out vestigingen where canRequestStart is true.
    // This way, we show fresh data (correct opening hours and badges) for records that have been stopped and are requested a second time
    const vestigingenMetAangevraagdeVestiging = vestigingen.filter((v) => !Vestiging.canRequestStart(v));

    const vestigingIds = new Set(vestigingenMetAangevraagdeVestiging.map((v) => v.beheerVestiging.id));
    const beheerVestigingenZonderAanvraag = beheerVestigingen.filter(
        (beheerVestiging) => !vestigingIds.has(beheerVestiging.id) && BeheerVestiging.isBeschikbaar(beheerVestiging)
    );

    const vestigingenZonderAangevraagdeVestiging = mapVestigingen(beheerVestigingenZonderAanvraag);
    const manualVestigingen = mapVestigingen(Object.values(manualStations).flat());

    const enrichedVestigingen = enrichVestigingen(
        [...vestigingenMetAangevraagdeVestiging, ...vestigingenZonderAangevraagdeVestiging, ...manualVestigingen],
        localOpenTotUpdates
    );

    const vestigingenByStation = Vestiging.groupByStation(enrichedVestigingen);
    const selectedCount = vestigingenIdSelection.length;

    const onVestigingenSelectionChange = useCallback((vestigingId: number, selected: boolean) => {
        setVestigingenIdSelection((prev) => (selected ? [...prev, vestigingId] : prev.filter((id) => id !== vestigingId)));
    }, []);

    const getSelectableVestigingIds = useCallback(
        (stationCode: string) => {
            const stationVestigingen = vestigingenByStation[stationCode] || [];
            return stationVestigingen.filter((v) => v.isSelectableForSelectAll).map((v) => v.beheerVestiging.id);
        },
        [vestigingenByStation]
    );

    const handleSelectAllForStation = useCallback(
        (stationCode: string) => {
            const selectableVestigingIds = getSelectableVestigingIds(stationCode);
            setVestigingenIdSelection((prev) => [...new Set([...prev, ...selectableVestigingIds])]);
        },
        [getSelectableVestigingIds]
    );

    const handleDeselectAllForStation = useCallback(
        (stationCode: string) => {
            const selectableVestigingIds = getSelectableVestigingIds(stationCode);
            setVestigingenIdSelection((prev) => prev.filter((id) => !selectableVestigingIds.includes(id)));
        },
        [getSelectableVestigingIds]
    );

    const handleUpdateOpeningstijd = useCallback((vestiging: Vestiging, nieuwOpenTot: string) => {
        setLocalOpenTotUpdates((prev) => ({
            ...prev,
            [vestiging.beheerVestiging.id]: nieuwOpenTot,
        }));
        return Promise.resolve();
    }, []);

    const handleSubmitRequest = useCallback(async () => {
        if (vestigingenIdSelection.length === 0) return;

        setRequestError(null);
        try {
            await requestVestigingenMutation.mutateAsync({
                dossierId: dossierId,
                vestigingen: vestigingenIdSelection.map((vestigingId) => {
                    const localOpenTotUpdate = localOpenTotUpdates[vestigingId];
                    return {
                        vestigingId,
                        aangepasteOpenTot: localOpenTotUpdate,
                    };
                }),
            });
            setVestigingenIdSelection([]);
            setManualStations({});
        } catch (error) {
            setRequestError(error as Error);
        }
    }, [dossierId, vestigingenIdSelection, requestVestigingenMutation, localOpenTotUpdates]);

    const handleAddStation = useCallback(
        async (stationCode: string) => {
            const lowercaseStationCode = stationCode.toLowerCase();
            const existingBetrokkenStation = betrokkenStations?.some((bs) => bs.stationsCode.toLowerCase() === lowercaseStationCode);
            const existingManualStation = Boolean(manualStations[lowercaseStationCode]);

            if (existingBetrokkenStation || existingManualStation) {
                return;
            }

            try {
                const manualVestigingen = await getLazyVestigingenByStations([stationCode]);
                setManualStations((prev) => ({
                    ...prev,
                    [lowercaseStationCode]: BeheerVestiging.filterBeschikaaar(manualVestigingen),
                }));
                setRequestError(null);
            } catch (error) {
                setRequestError(error as Error);
                return;
            }
        },
        [betrokkenStations, manualStations, getLazyVestigingenByStations]
    );

    // manual stations should be rendered last
    const manualStationCodes = Object.keys(manualStations);
    const sortedEntries = Object.entries(vestigingenByStation).sort(([stationCodeA, _vestigingA], [stationCodeB, _vestigingB]) => {
        const aIsManual = manualStationCodes.includes(stationCodeA.toLowerCase());
        const bIsManual = manualStationCodes.includes(stationCodeB.toLowerCase());
        if (aIsManual && !bIsManual) return 1;
        if (!aIsManual && bIsManual) return -1;
        return 0;
    });

    return (
        <DossierDetailsPanel>
            <DossierDetailsHeader section="Faciliteiten" showBackButton />
            <DossierDetailsContent className="space-y-6">
                {Object.keys(vestigingenByStation).length === 0 ? (
                    <EmptyState>Geen faciliteiten beschikbaar voor dit dossier.</EmptyState>
                ) : (
                    sortedEntries.map(([stationCode, vestigingen]) => {
                        return (
                            <FaciliteitenDetailsForStation
                                key={stationCode}
                                stationCode={stationCode}
                                vestigingen={vestigingen}
                                vestigingenIdSelection={vestigingenIdSelection}
                                onVestigingenSelectionChange={onVestigingenSelectionChange}
                                onSelectAllForStation={handleSelectAllForStation}
                                onDeselectAllForStation={handleDeselectAllForStation}
                                onUpdateOpeningstijd={handleUpdateOpeningstijd}
                            />
                        );
                    })
                )}

                {requestError && <ErrorDisplay error={requestError} title="Fout bij het aanvragen van faciliteiten" />}

                <div className="flex flex-col items-start justify-between gap-2">
                    <Button variant="secondary" onClick={() => setAddStationDialogOpen(true)}>
                        <Icon name="station" alt="station" strokeWidth="thick" className="text-content-interactive-default" />
                        Station toevoegen
                    </Button>

                    <span className="mt-4 text-sm text-gray-600" data-testid="faciliteiten-counter">
                        {selectedCount} faciliteiten geselecteerd
                    </span>
                    <Button
                        variant="primary"
                        onClick={handleSubmitRequest}
                        disabled={selectedCount === 0 || requestVestigingenMutation.isPending}
                    >
                        {requestVestigingenMutation.isPending ? (
                            "Aanvragen versturen..."
                        ) : (
                            <span className="flex items-center gap-2">
                                <Icon name="send" alt="versturen" className="text-content-default-inverse" />
                                Aanvraag versturen
                            </span>
                        )}
                    </Button>
                </div>

                <AddStationDialog
                    open={addStationDialogOpen}
                    onClose={() => setAddStationDialogOpen(false)}
                    onAddStation={handleAddStation}
                />
            </DossierDetailsContent>
        </DossierDetailsPanel>
    );
}
